/**
 * 録音ボタンコンポーネント
 *
 * 文字起こしページで使用する録音開始/停止ボタン。
 * useAudioRecorder、useTranscriptionConnection、transcriptionMachineを統合したスマートコンポーネント。
 * XStateを信頼できる単一情報源として状態管理を行います。
 */

'use client';

import { useMachine } from '@xstate/react';
import { Mic } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/components/AuthProvider';
import { AiUsageRemainingBadge } from '@/components/transcribe/AiUsageRemainingBadge';
import { Button } from '@/components/ui/button';
import { useOpusRecorder } from '@/hooks/useOpusRecorder';
import { usePcmRecorder } from '@/hooks/usePcmRecorder';
import { useInvalidateAiUsage } from '@/hooks/useAiUsageRemaining';
import { useTranscriptionAutoReconnect } from '@/hooks/useTranscriptionAutoReconnect';
import { useTranscriptionConnection } from '@/hooks/useTranscriptionConnection';
import { resetTranscriptionRecordingStateAfterStop } from '@/hooks/useTranscriptionRecordingSession';
import { supportsAudioWorklet } from '@/lib/audioCapabilityDetector';
import { logger } from '@/lib/logger';
import { transcriptionMachine } from '@/machines/transcriptionMachine';

// TODO(perf): 1h55m でのプリエンプティブ再接続は将来検討。
// 現状は close → reconnect (grace_wait 8s 以内) で 2h 境界を吸収する設計。

import type { AudioSource } from '@/hooks/useOpusRecorder';
import type { Speech2textInfo, TranscriptionPipelineState } from '@/hooks/useTranscriptionConnection';

const ACTIVE_RECORDING_SESSION_KEY = 'transcription.activeRecording.v1';
const PENDING_RECORDING_START_KEY = 'transcription.pendingRecordingStart.v1';
const ACTIVE_RECORDING_RESUME_WINDOW_RECORDING_MS = 60_000;
const ACTIVE_RECORDING_RESUME_WINDOW_PROCESSING_MS = 30 * 60_000;
const MAX_RECORDING_DURATION_MS = 3 * 60 * 60 * 1000;
const MIN_RECORDING_DURATION_MS = 60_000;
const FINAL_FALLBACK_POLL_INTERVALS_MS = [2000, 5000, 10000] as const;

function isMicrophonePermissionDenied(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'NotAllowedError'
  );
}

function getRecordingErrorMetadata(error: unknown) {
  const errorRecord = typeof error === 'object' && error !== null ? error : null;
  return {
    error:
      errorRecord && 'message' in errorRecord ? String(errorRecord.message) : String(error),
    errorName: errorRecord && 'name' in errorRecord ? String(errorRecord.name) : undefined,
  };
}

function logRecordingStartFailure(error: unknown): void {
  if (isMicrophonePermissionDenied(error)) {
    return;
  }
  logger.error('[RecordButton] startAudioRecording() failed', getRecordingErrorMetadata(error));
}

function logRecordingError(error: Error): void {
  if (isMicrophonePermissionDenied(error)) {
    logger.warn(
      '[RecordButton] Microphone permission was not granted',
      getRecordingErrorMetadata(error)
    );
    return;
  }
  logger.error('[RecordButton] Error occurred', error);
}

function getRecordingErrorMessage(error: Error): {
  description?: string;
  message: string;
} {
  if (isMicrophonePermissionDenied(error)) {
    return {
      message: 'マイクへのアクセスが許可されていません',
      description: 'ブラウザのサイト設定でマイクを許可してから、もう一度お試しください。',
    };
  }
  if (error.message.includes('WebSocket URL not configured')) {
    return { message: '設定エラー: WebSocket URLが設定されていません。管理者に問い合わせてください。' };
  }
  if (error.message.includes('Failed to get WebSocket token')) {
    return { message: '認証エラー: ログインし直してください。' };
  }
  if (error.message.includes('meetingId is not available')) {
    return { message: '接続エラー: 再度お試しください。' };
  }
  if (error.message === 'Connection timeout') {
    return { message: '接続がタイムアウトしました。ネットワークを確認して再試行してください。' };
  }
  return { message: `録音を開始できませんでした: ${error.message}` };
}

type ActiveRecordingSessionPhase = 'recording' | 'processing';

type ActiveRecordingSession = {
  meetingId: string;
  nextChunkSeq: number;
  updatedAt: number;
  phase: ActiveRecordingSessionPhase;
};

function requiresAudioSourceReselection(
  phase: ActiveRecordingSessionPhase, audioSource: AudioSource, stream: MediaStream | null,
): boolean {
  return phase === 'recording' && audioSource !== 'mic' &&
    !stream?.getAudioTracks().some((track) => track.readyState === 'live');
}

function readActiveRecordingSession(): ActiveRecordingSession | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_RECORDING_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ActiveRecordingSession>;
    const phase = parsed?.phase === 'processing' ? 'processing' : 'recording';
    if (
      typeof parsed?.meetingId !== 'string' ||
      !parsed.meetingId ||
      typeof parsed?.nextChunkSeq !== 'number' ||
      !Number.isFinite(parsed.nextChunkSeq) ||
      parsed.nextChunkSeq < 0 ||
      typeof parsed?.updatedAt !== 'number' ||
      !Number.isFinite(parsed.updatedAt)
    ) {
      return null;
    }
    if (Date.now() - parsed.updatedAt > getResumeWindowMs(phase)) {
      clearActiveRecordingSession();
      return null;
    }
    return {
      meetingId: parsed.meetingId,
      nextChunkSeq: Math.trunc(parsed.nextChunkSeq),
      updatedAt: Math.trunc(parsed.updatedAt),
      phase,
    };
  } catch {
    return null;
  }
}

function writeActiveRecordingSession(session: ActiveRecordingSession): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      ACTIVE_RECORDING_SESSION_KEY,
      JSON.stringify(session)
    );
  } catch {
    // noop
  }
}

function clearActiveRecordingSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(ACTIVE_RECORDING_SESSION_KEY);
  } catch {
    // noop
  }
}

function getResumeWindowMs(phase: ActiveRecordingSessionPhase): number {
  return phase === 'processing'
    ? ACTIVE_RECORDING_RESUME_WINDOW_PROCESSING_MS
    : ACTIVE_RECORDING_RESUME_WINDOW_RECORDING_MS;
}

interface RecordButtonProps {
  /** 録音完了時のコールバック（文字起こし結果を返す） */
  onComplete?: (transcript: string) => void;
  /** 処理完了時のコールバック（状態のみ反映） */
  onProcessingComplete?: () => void;
  /** エラー発生時のコールバック */
  onError?: (error: Error) => void;
  /** 録音開始時のコールバック */
  onRecordingStart?: () => void;
  /** 利用確認前に音声の取得が始まったことを通知する。 */
  onAudioCaptureStart?: () => void;
  /** 録音停止時のコールバック */
  onRecordingStop?: () => void;
  /** 文字起こし結果が更新されたときのコールバック（リアルタイム更新用） */
  onTranscriptUpdate?: (transcript: string) => void;
  /** 生の文字起こしが更新されたときのコールバック */
  onRawTranscriptUpdate?: (transcript: string) => void;
  /** Gemini整形済みの文字起こしが更新されたときのコールバック */
  onFormattedTranscriptUpdate?: (transcript: string) => void;
  /** commit state（確定/暫定/リビジョン）が更新されたときのコールバック */
  onCommitStateUpdate?: (state: {
    revision: number;
    committedText: string;
    draftText: string;
  }) => void;
  /** speech2text のバージョン情報を受信したときのコールバック */
  onSpeech2textInfo?: (info: Speech2textInfo) => void;
  /** 集約パイプライン状態が更新されたときのコールバック */
  onPipelineStateUpdate?: (state: TranscriptionPipelineState) => void;
  /** タブ音声ラベルが更新されたときのコールバック */
  onTabLabelChange?: (label: string | null) => void;
  /** 状態メッセージ更新時のコールバック */
  onStatusMessage?: (message: string | null) => void;
  /** 接続確立時に meetingId を通知するコールバック */
  onConnected?: (meetingId: string) => void;
  /** $final送信時のコールバック */
  onFinalRequested?: (meetingId: string) => void;
  /** 内部ステートマシンの状態が変化したときのコールバック */
  onStateValueChange?: (value: string) => void;
  /**
   * RecordButton の外部コントロールを公開するコールバック。
   * ページ遷移をまたいで同一の録音エンジンを操作する用途で使う。
   */
  onControlsChange?: (
    controls: {
      toggle: () => Promise<void>;
      start: () => Promise<void>;
      stop: () => Promise<void>;
      requestFinal?: () => Promise<void>;
    } | null
  ) => void;
  /** 追加のCSSクラス */
  className?: string;
  /** 音声ソース（デフォルト: 'mic'） */
  audioSource?: AudioSource;
  /** 事前選択済みタブ音声ストリーム */
  selectedTabStream?: MediaStream | null;
  /** Wait for persisted audio preferences before attempting reload recovery. */
  audioPreferencesReady?: boolean;
  /** 録音開始前に実行するガード */
  onBeforeStart?: () => boolean | Promise<boolean>;
}

/**
 * WS接続前に kakusill DB に meeting 行を予約する。
 * これにより speech2text Lambda 側の save 失敗に関わらず /api/pages/from-transcription が動作する。
 */
async function reserveMeeting(meetingId: string): Promise<void> {
  const res = await fetch('/api/transcriptions/reserve-recording', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ meetingId }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `reserve-recording failed: ${res.status}`);
  }
}

function resolvePendingRecordingStartKey({ authenticatedUser, loading, movingContext }: ReturnType<typeof useAuth>): string | null {
  if (loading || movingContext || !authenticatedUser?.currentWorkspaceId) return null;
  return `${PENDING_RECORDING_START_KEY}:${JSON.stringify([
    authenticatedUser.id, authenticatedUser.currentWorkspaceId, authenticatedUser.currentOrganizationId ?? null,
  ])}`;
}

function requireRecordingContext(key: string | null): string {
  if (!key) throw new Error('録音の保存先を確認中です。再試行してください。');
  return key;
}

// A client abort cannot roll back server-side usage confirmation. Keep the
// meeting until a successful, non-canceled start so retries remain idempotent.
function readPendingRecordingStart(key: string): string | null {
  try { return window.sessionStorage.getItem(key); }
  catch { return null; }
}

function persistPendingRecordingStart(key: string, meetingId: string | null): void {
  try {
    if (meetingId) window.sessionStorage.setItem(key, meetingId);
    else window.sessionStorage.removeItem(key);
  } catch { /* noop */ }
}

/** レコーダー起動成功後に録音開始と利用回数を確定する。 */
async function startMeetingRecording(meetingId: string, onUsageConsumed: () => void, signal: AbortSignal, pendingStartKey: string): Promise<void> {
  persistPendingRecordingStart(pendingStartKey, meetingId);
  const res = await fetch('/api/transcriptions/start-recording', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ meetingId }),
    signal,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `start-recording failed: ${res.status}`);
  }
  onUsageConsumed();
}

async function startRecorderAndConfirmUsage(
  startRecording: () => Promise<boolean>,
  meetingId: string,
  onUsageConsumed: () => void,
  signal: AbortSignal,
  pendingStartKey: string,
  onAudioCaptureStart?: () => void,
): Promise<boolean> {
  const started = await startRecording();
  if (!started) {
    return false;
  }
  signal.throwIfAborted();
  onAudioCaptureStart?.();
  await startMeetingRecording(meetingId, onUsageConsumed, signal, pendingStartKey);
  signal.throwIfAborted();
  return true;
}

/**
 * 録音ボタンコンポーネント
 *
 * @example
 * ```tsx
 * <RecordButton
 *   onComplete={(transcript) => console.log('完了:', transcript)}
 *   onError={(error) => console.error('エラー:', error)}
 *   onRecordingStart={() => console.log('録音開始')}
 *   onRecordingStop={() => console.log('録音停止')}
 * />
 * ```
 */
export const RecordButton: React.FC<RecordButtonProps> = ({
  onComplete,
  onProcessingComplete,
  onError,
  onRecordingStart,
  onAudioCaptureStart,
  onRecordingStop,
  onTranscriptUpdate,
  onRawTranscriptUpdate,
  onFormattedTranscriptUpdate,
  onCommitStateUpdate,
  onSpeech2textInfo,
  onPipelineStateUpdate,
  onTabLabelChange,
  onStatusMessage,
  onConnected,
  onFinalRequested,
  onStateValueChange,
  onControlsChange,
  className = '',
  audioSource = 'mic',
  selectedTabStream = null,
  audioPreferencesReady = true,
  onBeforeStart,
}) => {
  // Retrying a canceled confirmation is safe only in its original auth context.
  const pendingRecordingStartKey = resolvePendingRecordingStartKey(useAuth());
  const recordingStartKeyRef = useRef('');
  const preserveSessionOnErrorRef = useRef(false);

  // XState Machine
  const [state, send] = useMachine(transcriptionMachine);
  const invalidateAiUsage = useInvalidateAiUsage();
  const [usageConfirmedMeetingId, setUsageConfirmedMeetingId] = useState<string | null>(null);
  const usageConfirmationAbortRef = useRef<AbortController | null>(null);
  const [finalRequestedAt, setFinalRequestedAt] = useState<number | null>(null);
  const [lastFinalizationSignalAt, setLastFinalizationSignalAt] = useState<number | null>(null);
  const stateValueRef = useRef<string>(String(state.value));

  useEffect(() => {
    if (state.value !== 'recording') usageConfirmationAbortRef.current?.abort();
  }, [state.value]);

  useEffect(() => () => { usageConfirmationAbortRef.current?.abort(); }, []);

  useEffect(() => {
    onStateValueChange?.(String(state.value));
  }, [state.value, onStateValueChange]);

  useEffect(() => {
    stateValueRef.current = String(state.value);
  }, [state.value]);

  // PCM正規化を段階導入。未対応ブラウザでは自動的にopusへフォールバック。
  const requestedAudioFormat = (process.env.NEXT_PUBLIC_TRANSCRIPTION_AUDIO_FORMAT || 'pcm16').toLowerCase();
  const usePcm = requestedAudioFormat === 'pcm16' && supportsAudioWorklet();
  const requestedPcmChunkMsRaw = Number.parseInt(
    process.env.NEXT_PUBLIC_TRANSCRIPTION_PCM_CHUNK_MS || '500',
    10
  );
  const requestedPcmChunkMs = Number.isFinite(requestedPcmChunkMsRaw)
    ? Math.min(500, Math.max(200, requestedPcmChunkMsRaw))
    : 500;
  const finalWaitTimeoutMsRaw = Number.parseInt(
    process.env.NEXT_PUBLIC_TRANSCRIPTION_FINAL_WAIT_TIMEOUT_MS || '600000',
    10
  );
  const finalWaitTimeoutMs = Number.isFinite(finalWaitTimeoutMsRaw)
    ? Math.min(1800000, Math.max(120000, finalWaitTimeoutMsRaw))
    : 600000;
  const maxRecordingDurationMsRaw = Number.parseInt(
    process.env.NEXT_PUBLIC_TRANSCRIPTION_MAX_RECORDING_MS || String(MAX_RECORDING_DURATION_MS),
    10
  );
  const maxRecordingDurationMs = Number.isFinite(maxRecordingDurationMsRaw)
    ? Math.min(MAX_RECORDING_DURATION_MS, Math.max(MIN_RECORDING_DURATION_MS, maxRecordingDurationMsRaw))
    : MAX_RECORDING_DURATION_MS;
  const opusRecorder = useOpusRecorder({ audioSource, tabStream: selectedTabStream });
  const pcmRecorder = usePcmRecorder({
    audioSource,
    timeslice: requestedPcmChunkMs,
    tabStream: selectedTabStream,
  });
  const activeRecorder = usePcm ? pcmRecorder : opusRecorder;
  const {
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    audioChunks,
    error: recorderError,
    isRecording: actualIsRecording,
    tabLabel,
  } = activeRecorder;

  // useTranscriptionAutoReconnect の handleClose を ref 経由で渡す（フック宣言順の循環を避ける）
  const handleCloseRef = useRef<
    ((ev: { code: number; reason: string; wasClean: boolean }) => void) | null
  >(null);

  // useTranscriptionConnection
  const {
    connect,
    disconnect,
    sendChunk,
    requestFormat,
    requestFinal,
    requestTranscript,
    stopPolling,
    lastDefaultResponseAt,
    connectionState,
  } = useTranscriptionConnection({
    onTranscriptReceived: (text) => {
      send({ type: 'TRANSCRIPTION_RECEIVED', text });
    },
    onRawTranscriptReceived: (text) => {
      onRawTranscriptUpdate?.(text);
    },
    onFormattedTranscriptReceived: (text) => {
      onFormattedTranscriptUpdate?.(text);
      if (text.trim().length > 0) {
        // Final-only formatting completes asynchronously after $final.
        // Once formatted arrives, stop fallback polling to avoid needless WS traffic.
        stopFinalFallbackPolling();
        if (finalWaitTimeoutRef.current != null) {
          clearTimeout(finalWaitTimeoutRef.current);
          finalWaitTimeoutRef.current = null;
        }
      }
    },
    onCommitStateReceived: (state) => {
      onCommitStateUpdate?.(state);
    },
    onSpeech2textInfo,
    onPipelineStateReceived: (state) => {
      onPipelineStateUpdate?.(state);
    },
    onTranscriptionEvent: (event) => {
      if (
        event.type === 'transcript.finalized' ||
        event.type === 'transcript.formatted' ||
        event.type === 'transcript.completed'
      ) {
        setLastFinalizationSignalAt(Date.now());
      }
    },
    onConnected: (connectionId, meetingId) => {
      onConnected?.(meetingId);
      send({ type: 'CONNECTED', connectionId, meetingId });
    },
    onError: (error) => {
      send({ type: 'ERROR', error });
    },
    onClose: (event) => {
      handleCloseRef.current?.(event);
    },
  });

  const { handleClose: handleAutoReconnectClose } = useTranscriptionAutoReconnect({
    meetingId: state.context.meetingId ?? null,
    connect: ({ meetingId, resume }) => connect({ meetingId, resume }),
    enabled: state.value === 'recording',
    maxAttempts: 5,
    initialDelayMs: 500,
    onReconnecting: (attempt) => {
      toast.warning(`接続を復元中… (${attempt}/5)`, { duration: 3000 });
    },
    onReconnected: () => {
      // resume 後はサーバー側自動採番に切り替える（既存 resume パスと同じ意図）。
      // これを忘れると 2h 境界で seq が二重採番される可能性がある。
      useExplicitChunkSeqRef.current = false;
      toast.success('接続を復元しました');
    },
    onGiveUp: (err) => {
      toast.error('接続を復元できませんでした。録音を停止します。', {
        description: 'これまでの文字起こし結果は保存されている可能性があります。',
      });
      send({ type: 'ERROR', error: err });
    },
  });

  useEffect(() => {
    handleCloseRef.current = handleAutoReconnectClose;
  }, [handleAutoReconnectClose]);

  // 送信済みチャンク数を管理
  const sentChunksCountRef = useRef(0);
  // Resume直後は seq 欠番で停止しないよう、必要に応じてサーバー自動採番へ切り替える
  const useExplicitChunkSeqRef = useRef(true);
  // ローカル recorder バッファ上の読み取り位置（chunk_seq と分離）
  const localChunkCursorRef = useRef(0);
  const isSendingChunksRef = useRef(false);
  const audioChunksRef = useRef(audioChunks);

  // 録音開始の二重実行を防ぐフラグ
  const recordingStartedRef = useRef(false);
  const startRequestedRef = useRef(false);
  const completionNotifiedRef = useRef(false);
  // connecting effect の二重実行（StrictMode等）で connect() が多重発火しないようにする
  const connectStartedRef = useRef(false);
  // processing effect の二重実行等で format を多重発火しないようにする
  const formatRequestedRef = useRef(false);
  const finalWaitTimeoutRef = useRef<number | null>(null);
  const finalizeSignalTimeoutRef = useRef<number | null>(null);
  const finalFallbackPollTimeoutRef = useRef<number | null>(null);
  const finalFallbackPollAttemptRef = useRef(0);
  const recordingDurationTimeoutRef = useRef<number | null>(null);
  const resumeSessionRef = useRef<ActiveRecordingSession | null>(null);
  const resumeAttemptedRef = useRef(false);
  const resumeToProcessingRef = useRef(false);

  const persistActiveSession = useCallback((
    meetingId: string,
    nextChunkSeq: number,
    phase: ActiveRecordingSessionPhase = 'recording',
  ) => {
    if (!meetingId) {
      return;
    }
    writeActiveRecordingSession({
      meetingId,
      nextChunkSeq: Math.max(0, Math.trunc(nextChunkSeq)),
      updatedAt: Date.now(),
      phase,
    });
  }, []);

  const clearPersistedActiveSession = useCallback(() => {
    clearActiveRecordingSession();
  }, []);

  const stopFinalFallbackPolling = useCallback(() => {
    // Guard for any legacy polling interval that might still be active.
    stopPolling();
    if (finalFallbackPollTimeoutRef.current != null) {
      clearTimeout(finalFallbackPollTimeoutRef.current);
      finalFallbackPollTimeoutRef.current = null;
    }
    finalFallbackPollAttemptRef.current = 0;
  }, [stopPolling]);

  const scheduleFinalFallbackPolling = useCallback(() => {
    if (finalFallbackPollTimeoutRef.current != null) {
      return;
    }

    const scheduleNext = () => {
      if (stateValueRef.current !== 'processing') {
        finalFallbackPollTimeoutRef.current = null;
        finalFallbackPollAttemptRef.current = 0;
        return;
      }

      const intervalIndex = Math.min(
        finalFallbackPollAttemptRef.current,
        FINAL_FALLBACK_POLL_INTERVALS_MS.length - 1
      );
      const delayMs = FINAL_FALLBACK_POLL_INTERVALS_MS[intervalIndex];
      finalFallbackPollTimeoutRef.current = window.setTimeout(() => {
        finalFallbackPollTimeoutRef.current = null;
        if (stateValueRef.current !== 'processing') {
          return;
        }
        void requestTranscript().catch((error) => {
          logger.warn('[RecordButton] fallback requestTranscript() failed', {
            error: error instanceof Error ? error.message : String(error),
          });
        });
        finalFallbackPollAttemptRef.current = Math.min(
          finalFallbackPollAttemptRef.current + 1,
          FINAL_FALLBACK_POLL_INTERVALS_MS.length - 1
        );
        scheduleNext();
      }, delayMs);
    };

    scheduleNext();
  }, [requestTranscript]);

  useEffect(() => {
    onTabLabelChange?.(tabLabel ?? null);
  }, [tabLabel, onTabLabelChange]);

  useEffect(() => {
    if (requestedAudioFormat === 'pcm16' && !usePcm) {
      logger.warn('[RecordButton] PCM recorder requested but unsupported in this browser, fallback to Opus');
    }
  }, [requestedAudioFormat, usePcm]);

  useEffect(() => {
    if (requestedAudioFormat !== 'pcm16') {
      return;
    }
    if (requestedPcmChunkMsRaw !== requestedPcmChunkMs) {
      logger.warn('[RecordButton] NEXT_PUBLIC_TRANSCRIPTION_PCM_CHUNK_MS adjusted to safe range', {
        requested: requestedPcmChunkMsRaw,
        applied: requestedPcmChunkMs,
      });
    }
  }, [requestedAudioFormat, requestedPcmChunkMsRaw, requestedPcmChunkMs]);

  useEffect(() => {
    if (finalWaitTimeoutMsRaw !== finalWaitTimeoutMs) {
      logger.warn('[RecordButton] NEXT_PUBLIC_TRANSCRIPTION_FINAL_WAIT_TIMEOUT_MS adjusted to safe range', {
        requested: finalWaitTimeoutMsRaw,
        applied: finalWaitTimeoutMs,
      });
    }
  }, [finalWaitTimeoutMsRaw, finalWaitTimeoutMs]);

  useEffect(() => {
    if (maxRecordingDurationMsRaw !== maxRecordingDurationMs) {
      logger.warn('[RecordButton] NEXT_PUBLIC_TRANSCRIPTION_MAX_RECORDING_MS adjusted to safe range', {
        requested: maxRecordingDurationMsRaw,
        applied: maxRecordingDurationMs,
      });
    }
  }, [maxRecordingDurationMsRaw, maxRecordingDurationMs]);

  useEffect(() => {
    audioChunksRef.current = audioChunks;
  }, [audioChunks]);

  // Reload復元: 録音中にリロードされた場合は同じmeetingIdで再接続して継続する。
  useEffect(() => {
    if (!audioPreferencesReady || !pendingRecordingStartKey || state.value !== 'idle' || resumeAttemptedRef.current) {
      return;
    }
    resumeAttemptedRef.current = true;

    const persisted = readActiveRecordingSession();
    if (!persisted) {
      return;
    }
    resumeSessionRef.current = persisted;
    resumeToProcessingRef.current = persisted.phase === 'processing';
    sentChunksCountRef.current = persisted.nextChunkSeq;
    if (requiresAudioSourceReselection(persisted.phase, audioSource, selectedTabStream)) {
      // MediaStreams cannot survive reload. Preserve the meeting, but require
      // source reselection and an explicit start instead of microphone fallback.
      onStateValueChange?.('idle');
      onStatusMessage?.('入力先を選択して「録音を開始」を押すと、録音を再開できます。');
      return;
    }
    onStatusMessage?.('テキスト側を復元中...');
    toast.info(
      persisted.phase === 'processing'
        ? '文字起こし処理を復元しています…'
        : '録音セッションを復元しています…'
    );

    void (async () => {
      try {
        if (!resumeToProcessingRef.current) {
          const canStart = (await onBeforeStart?.()) ?? true;
          if (!canStart) {
            // Resume guard can fail transiently (e.g. auth/session still loading).
            // Keep resume payload and allow a later retry when dependencies update.
            resumeAttemptedRef.current = false;
            onStateValueChange?.('idle');
            onStatusMessage?.(null);
            return;
          }
        }
        send({ type: 'START_RECORDING' });
      } catch (error) {
        logger.warn('[RecordButton] resume onBeforeStart() failed', {
          error: error instanceof Error ? error.message : String(error),
        });
        resumeAttemptedRef.current = false;
        onStateValueChange?.('idle');
        onStatusMessage?.(null);
      }
    })();
  }, [state.value, audioPreferencesReady, pendingRecordingStartKey, audioSource, selectedTabStream, send, onBeforeStart, onStateValueChange, onStatusMessage]);

  useEffect(() => {
    if (state.value === 'idle' || state.value === 'connecting' || state.value === 'recording') {
      setLastFinalizationSignalAt(null);
    }
  }, [state.value]);

  // Keep the timeout tied to the attempt's state, not changing auth context.
  useEffect(() => {
    if (state.value !== 'connecting') return;
    const timeoutId = setTimeout(() => {
      send({ type: 'CONNECTION_FAILED', error: new Error('Connection timeout') });
    }, 120000);
    return () => clearTimeout(timeoutId);
  }, [state.value, send]);

  // Effect 1: 接続処理 (connecting状態)
  useEffect(() => {
    if (state.value !== 'connecting') {
      connectStartedRef.current = false;
      return;
    }

    if (connectStartedRef.current) {
      return;
    }
    connectStartedRef.current = true;

    // 前回セッションのWS/ポーリングが残っていると再開時に Already connected... になるため掃除する
    stopFinalFallbackPolling();
    disconnect();

    {
      const resumeSession = resumeSessionRef.current;
      preserveSessionOnErrorRef.current = Boolean(resumeSession);
      // Resume中は、リロード直前の未到達chunkによる seq 欠番を避けるため自動採番を優先する。
      const shouldUseAutoSequencing = Boolean(
        resumeSession && resumeSession.phase === 'recording'
      );
      useExplicitChunkSeqRef.current = !shouldUseAutoSequencing;
      if (resumeSession) {
        sentChunksCountRef.current = shouldUseAutoSequencing
          ? 0
          : Math.max(0, Math.trunc(resumeSession.nextChunkSeq));
      } else {
        // 新規セッション時は seq=0 から送る
        sentChunksCountRef.current = 0;
        useExplicitChunkSeqRef.current = true;
      }
      localChunkCursorRef.current = 0;
      isSendingChunksRef.current = false;

      const doConnect = async () => {
        const pendingStartKey = requireRecordingContext(pendingRecordingStartKey);
        recordingStartKeyRef.current = pendingStartKey;
        const pendingMeetingId = readPendingRecordingStart(pendingStartKey);
        const connectMeetingId = resumeSession?.meetingId ?? pendingMeetingId ?? crypto.randomUUID();

        if (!resumeSession) setUsageConfirmedMeetingId(null);
        // A pending confirmation already has a reservation. Reserving again
        // would reject a retry if that confirmation consumed the last quota.
        if (!resumeSession && !pendingMeetingId) await reserveMeeting(connectMeetingId);

        await connect(connectMeetingId);
      };

      doConnect()
        .then(() => {
          if (resumeSession) {
            persistActiveSession(
              resumeSession.meetingId,
              sentChunksCountRef.current,
              resumeSession.phase
            );
            if (resumeSession.phase === 'processing') {
              toast.success('文字起こし処理を再開しました');
            } else {
              toast.success('録音セッションを復元しました');
            }
            // Rehydrate transcript/log view immediately after resume.
            void requestTranscript().catch((error) => {
              logger.warn('[RecordButton] resume snapshot request failed', {
                error: error instanceof Error ? error.message : String(error),
                meetingId: resumeSession.meetingId,
              });
            });
          }
          resumeSessionRef.current = null;
        })
        .catch((error) => {
          resumeSessionRef.current = null;
          clearPersistedActiveSession();
          send({ type: 'CONNECTION_FAILED', error });
        });
    }
  }, [
    state.value,
    connect,
    disconnect,
    stopFinalFallbackPolling,
    send,
    persistActiveSession,
    clearPersistedActiveSession,
    requestTranscript,
    pendingRecordingStartKey,
  ]);

  // Effect 2: 録音開始 (recording状態)
  // React Strict Modeでの二重実行を防ぐため、recordingStartedRefを使用
  useEffect(() => {
    if (state.value === 'recording' && resumeToProcessingRef.current) {
      // processing 復元時は録音を再開せず、即座に processing へ戻して final を再送する。
      resumeToProcessingRef.current = false;
      send({ type: 'STOP_RECORDING' });
      return;
    }

    if (
      state.value === 'recording' &&
      !actualIsRecording &&
      !recordingStartedRef.current &&
      state.context.meetingId
    ) {
      recordingStartedRef.current = true;

      logger.info('[RecordButton] Starting recording', { meetingId: state.context.meetingId });

      const meetingId = state.context.meetingId;
      const pendingStartKey = recordingStartKeyRef.current;
      const controller = new AbortController();
      usageConfirmationAbortRef.current = controller;
      startRecorderAndConfirmUsage(startAudioRecording, meetingId, invalidateAiUsage, controller.signal, pendingStartKey, onAudioCaptureStart)
        .then((started) => {
          if (!started || controller.signal.aborted) {
            return;
          }
          preserveSessionOnErrorRef.current = true;
          persistPendingRecordingStart(pendingStartKey, null);
          setUsageConfirmedMeetingId(meetingId);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          logRecordingStartFailure(error);
          recordingStartedRef.current = false; // エラー時はフラグをリセット
          send({
            type: 'ERROR',
            error: error instanceof Error ? error : new Error(String(error)),
          });
        });
    }

    // recording状態から離れたらフラグをリセット
    if (state.value !== 'recording') {
      recordingStartedRef.current = false;
    }
  }, [
    state.value,
    state.context.meetingId,
    actualIsRecording,
    startAudioRecording,
    invalidateAiUsage,
    onAudioCaptureStart,
    send,
  ]);

  useEffect(() => {
    if (state.value === 'recording' && state.context.meetingId) {
      persistActiveSession(state.context.meetingId, sentChunksCountRef.current, 'recording');
    }
  }, [state.value, state.context.meetingId, persistActiveSession]);

  useEffect(() => {
    if (state.value !== 'recording') {
      if (recordingDurationTimeoutRef.current != null) {
        clearTimeout(recordingDurationTimeoutRef.current);
        recordingDurationTimeoutRef.current = null;
      }
      return;
    }

    recordingDurationTimeoutRef.current = window.setTimeout(() => {
      recordingDurationTimeoutRef.current = null;
      toast.error('録音時間の上限（3時間）に達したため自動停止しました。', {
        description: '保存処理中です。新しい録音は完了後に開始できます。',
        duration: Number.POSITIVE_INFINITY,
        closeButton: true,
      });
      send({ type: 'STOP_RECORDING' });
    }, maxRecordingDurationMs);

    return () => {
      if (recordingDurationTimeoutRef.current != null) {
        clearTimeout(recordingDurationTimeoutRef.current);
        recordingDurationTimeoutRef.current = null;
      }
    };
  }, [state.value, maxRecordingDurationMs, send]);

  // Effect 2.6: push-first + fallback polling
  // - processing中はイベント到着を優先し、$default は段階バックオフ (2s→5s→10s) で補助取得
  // - completed中は常時ポーリングせず、最終スナップショットを1回だけ取得
  useEffect(() => {
    if (state.value === 'processing') {
      scheduleFinalFallbackPolling();
      if (finalWaitTimeoutRef.current == null) {
        finalWaitTimeoutRef.current = window.setTimeout(() => {
          finalWaitTimeoutRef.current = null;
          stopFinalFallbackPolling();
        }, finalWaitTimeoutMs);
      }
      return undefined;
    }

    if (state.value === 'completed') {
      clearPersistedActiveSession();
      stopFinalFallbackPolling();
      if (finalWaitTimeoutRef.current != null) {
        clearTimeout(finalWaitTimeoutRef.current);
        finalWaitTimeoutRef.current = null;
      }
      void requestTranscript().catch((error) => {
        logger.warn('[RecordButton] final snapshot request failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
      return undefined;
    }

    if (state.value === 'idle' || state.value === 'error') {
      stopFinalFallbackPolling();
      setFinalRequestedAt(null);
      if (finalWaitTimeoutRef.current != null) {
        clearTimeout(finalWaitTimeoutRef.current);
        finalWaitTimeoutRef.current = null;
      }
      if (finalizeSignalTimeoutRef.current != null) {
        clearTimeout(finalizeSignalTimeoutRef.current);
        finalizeSignalTimeoutRef.current = null;
      }
    }

    return undefined;
  }, [
    state.value,
    scheduleFinalFallbackPolling,
    stopFinalFallbackPolling,
    finalWaitTimeoutMs,
    requestTranscript,
    clearPersistedActiveSession,
  ]);

  // Effect 3: チャンク送信 (audioChunks変化時)
  useEffect(() => {
    if (
      state.value !== 'recording' ||
      !state.context.meetingId ||
      usageConfirmedMeetingId !== state.context.meetingId
    ) {
      return undefined;
    }

    let cancelled = false;

    const flush = async () => {
      if (isSendingChunksRef.current) {
        return;
      }
      isSendingChunksRef.current = true;
      try {
        let failureCount = 0;
        while (!cancelled) {
          const chunks = audioChunksRef.current;
          if (localChunkCursorRef.current >= chunks.length) {
            break;
          }

          const chunk = chunks[localChunkCursorRef.current];

          try {
            // chunk_seq はサーバー側の通し番号、localChunkCursor は現在マウント内の配列位置。
            const chunkSeq = useExplicitChunkSeqRef.current
              ? sentChunksCountRef.current
              : undefined;
            await sendChunk(chunk, chunkSeq);
            localChunkCursorRef.current += 1;
            sentChunksCountRef.current += 1;
            if (state.context.meetingId) {
              persistActiveSession(state.context.meetingId, sentChunksCountRef.current, 'recording');
            }
            failureCount = 0;

            send({ type: 'CHUNK_SENT', chunk });
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            // WebSocket 切断中は失敗扱いせず flush を抜けて自動再接続の完了を待つ。
            // 再接続成功時に connectionState が変化し、Effect 3 が依存変更で再走する。
            const isDisconnected =
              message.includes('Not connected') || message.includes('WebSocket is not open');
            if (isDisconnected) {
              logger.info('[RecordButton] WS disconnected during send, awaiting auto-reconnect', {
                meetingId: state.context.meetingId,
                cursor: localChunkCursorRef.current,
                seq: sentChunksCountRef.current,
              });
              break;
            }

            failureCount += 1;
            logger.error('[RecordButton] Failed to send audio chunk', {
              meetingId: state.context.meetingId,
              seq: sentChunksCountRef.current,
              error: message,
            });

            if (failureCount >= 3) {
              send({
                type: 'ERROR',
                error: error instanceof Error ? error : new Error(String(error)),
              });
              break;
            }

            // Retry the same chunk/seq on next iteration. Avoid tight loop.
            await new Promise((resolve) => setTimeout(resolve, 500 * failureCount));
          }
        }
      } finally {
        isSendingChunksRef.current = false;
      }
    };

    void flush();

    return () => {
      cancelled = true;
    };
  }, [
    audioChunks.length,
    state.value,
    state.context.meetingId,
    sendChunk,
    send,
    persistActiveSession,
    // connectionState を依存に入れることで、自動再接続成功時 (disconnected→connected) に
    // flush が再走し、断中に溜まったチャンクが localChunkCursor 位置から再送される。
    connectionState,
    usageConfirmedMeetingId,
  ]);

  // Effect 4: 処理開始 (processing状態)
  useEffect(() => {
    if (state.value === 'processing') {
      // 録音停止
      stopAudioRecording();
      if (state.context.meetingId) {
        persistActiveSession(state.context.meetingId, sentChunksCountRef.current, 'processing');
      }
      const requestedAt = Date.now();
      setFinalRequestedAt(requestedAt);

      // $final を実行:
      // - 即時に S3 へ raw を保存
      // - Gemini整形はサーバ側で非同期に実行し、後で formatted_transcription が更新される
      requestFinal()
        .then(() => {
          // NOTE:
          // requestFinal() は「送信完了」であり「サーバ処理完了」ではない。
          // completed への遷移は、$default の次回レスポンス受信（lastDefaultResponseAt更新）を待って行う。
          if (state.context.meetingId) {
            onFinalRequested?.(state.context.meetingId);
          }

          // formatted_transcription が自動生成されない環境向けに、明示的に format を要求する（idempotent想定）
          if (!formatRequestedRef.current) {
            formatRequestedRef.current = true;
            requestFormat().catch((error) => {
              logger.warn('[RecordButton] requestFormat() failed', {
                error: error instanceof Error ? error.message : String(error),
              });
            });
          }

          requestTranscript().catch((error) => {
            logger.error('[RecordButton] Failed to request transcript after final', {
              error: error instanceof Error ? error.message : String(error),
            });
          });
        })
        .catch((error) => {
          setFinalRequestedAt(null);
          send({ type: 'ERROR', error });
        });
    }
  }, [
    state.value,
    state.context.meetingId,
    stopAudioRecording,
    requestFormat,
    requestFinal,
    requestTranscript,
    send,
    onFinalRequested,
    persistActiveSession,
  ]);

  useEffect(() => {
    if (state.value !== 'processing') {
      formatRequestedRef.current = false;
    }
  }, [state.value]);

  // Effect 4.5: final 送信後、finalized/formattedイベントまたは $default レスポンスで completed へ遷移
  useEffect(() => {
    if (state.value !== 'processing' || finalRequestedAt == null) {
      if (finalizeSignalTimeoutRef.current !== null) {
        clearTimeout(finalizeSignalTimeoutRef.current);
        finalizeSignalTimeoutRef.current = null;
      }
      return;
    }

    const finalizedByEvent =
      lastFinalizationSignalAt != null && lastFinalizationSignalAt >= finalRequestedAt;
    const finalizedByDefaultResponse =
      lastDefaultResponseAt != null && lastDefaultResponseAt >= finalRequestedAt;
    if (finalizedByEvent || finalizedByDefaultResponse) {
      if (finalizeSignalTimeoutRef.current !== null) {
        clearTimeout(finalizeSignalTimeoutRef.current);
        finalizeSignalTimeoutRef.current = null;
      }
      setFinalRequestedAt(null);
      send({ type: 'FINAL_REQUESTED' });
      return;
    }

    if (finalizeSignalTimeoutRef.current === null) {
      // サーバー応答欠落時に processing が無限継続しないようにタイムアウトする。
      finalizeSignalTimeoutRef.current = window.setTimeout(() => {
        finalizeSignalTimeoutRef.current = null;
        setFinalRequestedAt(null);
        send({ type: 'ERROR', error: new Error('final timeout: finalize signal not received') });
      }, 30_000);
    }

    return () => {
      if (finalizeSignalTimeoutRef.current !== null) {
        clearTimeout(finalizeSignalTimeoutRef.current);
        finalizeSignalTimeoutRef.current = null;
      }
    };
  }, [state.value, finalRequestedAt, lastDefaultResponseAt, lastFinalizationSignalAt, send]);

  // Effect 5: 完了時コールバック (completed状態)
  useEffect(() => {
    if (state.value !== 'completed') {
      completionNotifiedRef.current = false;
      return;
    }
    // 新規録音画面や遅延 transcript による再描画では、完了を再通知しない。
    if (completionNotifiedRef.current) return;
    completionNotifiedRef.current = true;
    if (state.context.transcript) onComplete?.(state.context.transcript);
    onProcessingComplete?.();
  }, [state.value, state.context.transcript, onComplete, onProcessingComplete]);

  // Effect 6: エラー時コールバック (error状態)
  useEffect(() => {
    if (state.value === 'error' && state.context.error) {
      const error = state.context.error;
      // Start failures can reset; active/recovered sessions retain their transcript.
      if (!preserveSessionOnErrorRef.current) resetTranscriptionRecordingStateAfterStop();
      logRecordingError(error);
      const { message, description } = getRecordingErrorMessage(error);

      toast.error(message, {
        description,
        action: {
          label: '再試行',
          onClick: () => send({ type: 'RESET' }),
        },
      });

      onError?.(new Error(description ? `${message}。${description}` : message));
    }
  }, [state.value, state.context.error, onError, send]);

  // Effect 7: recorderErrorをXStateに反映
  useEffect(() => {
    if (recorderError) {
      send({ type: 'ERROR', error: recorderError });
    }
  }, [recorderError, send]);

  // 利用回数確認など、レコーダー起動後の開始処理が失敗した場合もマイクを解放する。
  useEffect(() => {
    if (state.value === 'error' && actualIsRecording) {
      stopAudioRecording();
    }
  }, [state.value, actualIsRecording, stopAudioRecording]);

  // Effect 8: 録音開始/停止コールバック
  useEffect(() => {
    if (
      state.value === 'recording' &&
      state.context.meetingId &&
      usageConfirmedMeetingId === state.context.meetingId
    ) {
      onRecordingStart?.();
    }
  }, [state.value, state.context.meetingId, usageConfirmedMeetingId, onRecordingStart]);

  useEffect(() => {
    if (state.value === 'processing') {
      onRecordingStop?.();
    }
  }, [state.value, onRecordingStop]);

  useEffect(() => {
    if (state.value === 'error') {
      clearPersistedActiveSession();
      // フォールバックポーリング停止
      stopFinalFallbackPolling();

      // エラー時は録音を確実に停止
      if (actualIsRecording) {
        stopAudioRecording();
      }
    }
  }, [state.value, actualIsRecording, stopAudioRecording, stopFinalFallbackPolling, clearPersistedActiveSession]);

  // 中間文字起こし結果の通知
  useEffect(() => {
    if (state.context.transcript && onTranscriptUpdate) {
      onTranscriptUpdate(state.context.transcript);
    }
  }, [state.context.transcript, onTranscriptUpdate]);

  const runStart = useCallback(async () => {
    const currentState = stateValueRef.current;
    if (startRequestedRef.current || !(currentState === 'idle' || currentState === 'completed' || currentState === 'error')) {
      return;
    }
    startRequestedRef.current = true;
    try {
      requireRecordingContext(pendingRecordingStartKey);
      const canStart = (await onBeforeStart?.()) ?? true;
      if (!canStart) return;
      if (currentState === 'error') send({ type: 'RESET' });
      stateValueRef.current = 'connecting';
      send({ type: 'START_RECORDING' });
    } finally {
      startRequestedRef.current = false;
    }
  }, [onBeforeStart, pendingRecordingStartKey, send]);

  const runStop = useCallback(async () => {
    if (
      stateValueRef.current !== 'recording' ||
      !state.context.meetingId
    ) {
      return;
    }
    if (usageConfirmedMeetingId !== state.context.meetingId) {
      if (!actualIsRecording) return;
      usageConfirmationAbortRef.current?.abort();
      stopAudioRecording();
      disconnect();
      send({ type: 'ERROR', error: new Error('録音の開始を中止しました。再試行できます。') });
      return;
    }
    send({ type: 'STOP_RECORDING' });
  }, [send, state.context.meetingId, usageConfirmedMeetingId, actualIsRecording, stopAudioRecording, disconnect]);

  const runToggle = useCallback(async () => {
    const currentState = stateValueRef.current;
    if (currentState === 'recording') {
      await runStop();
      return;
    }
    if (currentState === 'idle' || currentState === 'completed') {
      await runStart();
      return;
    }
    if (currentState === 'error') {
      await runStart();
    }
  }, [runStart, runStop]);

  // 外部コントロール（トグル）を公開
  const handleClickRef = useRef(runToggle);
  useEffect(() => {
    handleClickRef.current = runToggle;
  }, [runToggle]);

  // requestFinal は WS 上で同期 send（ws.send だけで finalize を要求）するため、
  // pagehide のように非同期完結を待てないシーンから呼ぶ用にコントロールに公開する。
  const requestFinalRef = useRef(requestFinal);
  useEffect(() => {
    requestFinalRef.current = requestFinal;
  }, [requestFinal]);

  useEffect(() => {
    if (!onControlsChange) {
      return;
    }
    onControlsChange({
      toggle: () => handleClickRef.current(),
      start: () => runStart(),
      stop: () => runStop(),
      requestFinal: () => requestFinalRef.current(),
    });
    return () => {
      onControlsChange(null);
    };
  }, [onControlsChange, runStart, runStop]);

  // ボタンテキストの計算
  const getButtonText = () => {
    switch (state.value) {
      case 'idle':
        return '録音を開始';
      case 'connecting':
        return '接続中...';
      case 'recording':
        return usageConfirmedMeetingId === state.context.meetingId ? '停止' : '音声取得中・停止';
      case 'processing':
        return '処理中...';
      case 'completed':
        return '録音を開始';
      case 'error':
        return '録音を開始';
      default:
        return '録音を開始';
    }
  };

  // ボタン無効化の計算
  const isButtonDisabled = () => {
    return (
      state.value === 'connecting' ||
      state.value === 'processing' ||
      (state.value === 'recording' && !actualIsRecording && usageConfirmedMeetingId !== state.context.meetingId)
    );
  };

  // ボタンvariantの計算
  const getButtonVariant = (): 'default' | 'destructive' | 'secondary' => {
    switch (state.value) {
      case 'idle':
      case 'completed':
        return 'default';
      case 'connecting':
      case 'processing':
        return 'secondary';
      default:
        return 'destructive';
    }
  };

  // aria-labelの動的変更
  const getAriaLabel = () => {
    const audioSourceLabel =
      audioSource === 'both'
        ? 'システム音声+マイク'
        : audioSource === 'tab'
          ? 'システム音声のみ'
          : 'マイクのみ';

    switch (state.value) {
      case 'idle':
        return `録音を開始 (${audioSourceLabel})`;
      case 'connecting':
        return 'WebSocketに接続中';
      case 'recording':
        return usageConfirmedMeetingId === state.context.meetingId
          ? `録音を停止 (${audioSourceLabel})`
          : '音声の取得を停止（利用確認中）';
      case 'processing':
        return '文字起こしを処理中';
      case 'completed':
        return '文字起こし完了';
      case 'error':
        return `エラー: ${state.context.error?.message || '不明なエラー'}`;
      default:
        return `録音を開始 (${audioSourceLabel})`;
    }
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant={getButtonVariant()}
        onClick={() => {
          void runToggle().catch((error) => {
            send({ type: 'ERROR', error: error instanceof Error ? error : new Error(String(error)) });
          });
        }}
        disabled={isButtonDisabled()}
        className={className}
        aria-label={getAriaLabel()}
      >
        {state.value === 'recording' ? (
          <div className="soundbar-container mr-2" aria-hidden="true">
            {[0, 0.2, 0.4, 0.15, 0.35].map((delay, i) => (
              <span
                key={i}
                className="soundbar-bar bg-current"
                style={{ animationDelay: `${delay}s` }}
              />
            ))}
          </div>
        ) : (
          <Mic className="h-4 w-4 mr-2" />
        )}
        <span>{getButtonText()}</span>
      </Button>
      <AiUsageRemainingBadge compact />
    </div>
  );
};
